import { useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { ArrowLeft, User, Camera, Save, Mail } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { updateUser } from '@/lib/firestore'

export default function SettingsPage() {
  const { user, isLoaded } = useUser()
  const navigate = useNavigate()
  const { toast } = useToast()
  
  const [displayName, setDisplayName] = useState(
    (user?.unsafeMetadata?.displayName as string) || 
    user?.fullName || 
    user?.firstName || 
    ''
  )
  const [email, setEmail] = useState(user?.primaryEmailAddress?.emailAddress || '')
  const [isUpdating, setIsUpdating] = useState(false)

  const handleUpdateProfile = async () => {
    if (!user?.id || !displayName.trim()) return

    try {
      setIsUpdating(true)
      
      // Update Clerk profile - use unsafeMetadata for display name
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          displayName: displayName.trim(),
        }
      })

      // Update Firestore user data with latest Clerk profile info
      await updateUser(user.id, {
        displayName: displayName.trim(),
        profileImageUrl: user.imageUrl,
        email: user.primaryEmailAddress?.emailAddress || email,
      })

      toast({
        title: "Profile Updated!",
        description: "Your profile has been successfully updated.",
        duration: 3000,
      })
    } catch (error) {
      console.error('Error updating profile:', error)
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
    } finally {
      setIsUpdating(false)
    }
  }

  const formatDate = (timestamp: number | Date) => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date)
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#42a5f5] border-t-transparent rounded-full"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Dashboard</span>
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Profile Section */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center space-x-2 mb-6">
              <User className="h-5 w-5 text-[#42a5f5]" />
              <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
            </div>

            <div className="space-y-6">
              {/* Profile Image */}
              <div className="flex items-center space-x-6">
                <div className="relative">
                  {user?.imageUrl ? (
                    <img
                      src={user.imageUrl}
                      alt="Profile"
                      className="w-20 h-20 rounded-full object-cover border-4 border-gray-200"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[#42a5f5] flex items-center justify-center text-white font-bold text-xl border-4 border-gray-200">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1.5 border border-gray-300 shadow-sm">
                    <Camera className="h-4 w-4 text-gray-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Profile Photo</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Your profile photo is managed through your account settings. It will automatically sync to SportSpot.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => window.open(user?.imageUrl || '#', '_blank')}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Update Photo
                  </Button>
                </div>
              </div>

              {/* Display Name */}
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                  className="max-w-md"
                />
                <p className="text-sm text-gray-500">
                  This name will be shown to other users in groups and leaderboards.
                </p>
              </div>

              {/* Email (Read-only) */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative max-w-md">
                  <Input
                    id="email"
                    value={email}
                    disabled
                    className="pr-10"
                  />
                  <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">
                  Email cannot be changed here. Please update it in your account settings.
                </p>
              </div>

              {/* Save Button */}
              <div className="flex items-center space-x-4 pt-4">
                <Button
                  onClick={handleUpdateProfile}
                  disabled={isUpdating || !displayName.trim()}
                  className="bg-[#42a5f5] hover:bg-[#1976d2]"
                >
                  {isUpdating ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDisplayName(
                      (user?.unsafeMetadata?.displayName as string) || 
                      user?.fullName || 
                      user?.firstName || 
                      ''
                    )
                    setEmail(user?.primaryEmailAddress?.emailAddress || '')
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}